import { useEffect, useState } from 'react';
import { FlaskConical, Loader2, Save } from 'lucide-react';
import {
  storageConfigAdminApi,
  type StorageConfigPayload,
  type StorageConfigView,
} from '../services/videoAdminApi';
import toast from '../../../components/Toast';

export default function StorageConfigTab() {
  const [config, setConfig] = useState<StorageConfigView | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [minioEndpoint, setMinioEndpoint] = useState('');
  const [minioPort, setMinioPort] = useState<number | null>(null);
  const [minioUseSSL, setMinioUseSSL] = useState(true);
  const [minioAccessKey, setMinioAccessKey] = useState('');
  const [minioSecretKey, setMinioSecretKey] = useState('');
  const [minioBucket, setMinioBucket] = useState('');
  const [minioPublicDomain, setMinioPublicDomain] = useState('');
  const [minioIsPrivate, setMinioIsPrivate] = useState(true);
  const [minioSignedUrlExpiresIn, setMinioSignedUrlExpiresIn] = useState(3600);

  const load = async () => {
    setLoading(true);
    try {
      const data = await storageConfigAdminApi.get();
      setConfig(data);
      setMinioEndpoint(data.minioEndpoint);
      setMinioPort(data.minioPort);
      setMinioUseSSL(data.minioUseSSL);
      setMinioAccessKey(data.minioAccessKey);
      setMinioBucket(data.minioBucket);
      setMinioPublicDomain(data.minioPublicDomain);
      setMinioIsPrivate(data.minioIsPrivate);
      setMinioSignedUrlExpiresIn(data.minioSignedUrlExpiresIn);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '加载配置失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const buildPayload = (): StorageConfigPayload => ({
    minioEndpoint: minioEndpoint.trim() || null,
    minioPort,
    minioUseSSL,
    minioAccessKey: minioAccessKey.trim() || null,
    minioSecretKey: minioSecretKey.trim() || undefined,
    minioBucket: minioBucket.trim() || null,
    minioPublicDomain: minioPublicDomain.trim() || null,
    minioIsPrivate,
    minioSignedUrlExpiresIn: Number(minioSignedUrlExpiresIn) || 3600,
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await storageConfigAdminApi.update(buildPayload());
      setConfig(updated);
      setMinioSecretKey('');
      toast.success('MinIO 配置已保存');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!minioEndpoint || !minioAccessKey || (!minioSecretKey && !config?.minioSecretKeyMasked) || !minioBucket) {
      toast.error('请填写 MinIO 连接信息');
      return;
    }
    setTesting(true);
    try {
      const result = await storageConfigAdminApi.test(buildPayload());
      if (result.passed) toast.success(result.message || 'MinIO 连接通过');
      else toast.error(result.message || 'MinIO 连接失败');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'MinIO 连接测试失败');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <div className="py-12 text-center text-light-muted"><Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin" />加载中</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-light-text">MinIO 存储配置</h2>
        <p className="mt-1 text-sm text-light-muted">业务图片和视频统一存储在 MinIO。历史 COS 数据仅可通过媒体迁移工具读取和搬迁。</p>
      </div>
      <div className="space-y-4 rounded-2xl border border-light-border bg-white p-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Endpoint"><input type="text" value={minioEndpoint} onChange={(event) => setMinioEndpoint(event.target.value)} className="input-field w-full" placeholder="minio.example.com" /></FormField>
          <FormField label="端口（可选）"><input type="number" value={minioPort ?? ''} onChange={(event) => setMinioPort(event.target.value ? Number(event.target.value) : null)} className="input-field w-full" placeholder="9000" min={1} max={65535} /></FormField>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Access Key"><input type="text" value={minioAccessKey} onChange={(event) => setMinioAccessKey(event.target.value)} className="input-field w-full font-mono text-sm" placeholder="MINIO_ACCESS_KEY" /></FormField>
          <FormField label="Secret Key" hint={config?.minioSecretKeyMasked ? `当前：${config.minioSecretKeyMasked}（留空不修改）` : ''}><input type="password" value={minioSecretKey} onChange={(event) => setMinioSecretKey(event.target.value)} className="input-field w-full font-mono text-sm" placeholder="留空 = 保持不变" /></FormField>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Bucket"><input type="text" value={minioBucket} onChange={(event) => setMinioBucket(event.target.value)} className="input-field w-full" placeholder="zhitu" /></FormField>
          <FormField label="公开访问域名（可选）" hint="可填写 CDN 或对象访问域名"><input type="text" value={minioPublicDomain} onChange={(event) => setMinioPublicDomain(event.target.value)} className="input-field w-full" placeholder="https://storage.example.com" /></FormField>
        </div>
        <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-3">
          <label className="flex cursor-pointer items-center gap-2 pt-2 text-sm text-light-text"><input type="checkbox" checked={minioUseSSL} onChange={(event) => setMinioUseSSL(event.target.checked)} className="h-4 w-4" />使用 SSL（HTTPS）</label>
          <label className="flex cursor-pointer items-center gap-2 pt-2 text-sm text-light-text"><input type="checkbox" checked={minioIsPrivate} onChange={(event) => setMinioIsPrivate(event.target.checked)} className="h-4 w-4" />私有桶（使用签名 URL）</label>
          <FormField label="签名 URL 有效期（秒）" hint="60 ~ 604800"><input type="number" value={minioSignedUrlExpiresIn} onChange={(event) => setMinioSignedUrlExpiresIn(Number(event.target.value))} className="input-field w-full" min={60} max={7 * 24 * 3600} /></FormField>
        </div>
      </div>
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button onClick={handleTest} disabled={testing} className="btn-secondary flex items-center justify-center gap-2">{testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />}测试 MinIO 连接</button>
        <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center justify-center gap-2">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}保存</button>
      </div>
    </div>
  );
}

function FormField({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return <div><label className="mb-1.5 block text-sm font-medium text-light-text">{label}{hint && <span className="ml-2 text-xs text-light-muted">{hint}</span>}</label>{children}</div>;
}
